import { SET_CURRENT_LANGUAGE, SET_LANGUAGES, setCurrentLanguage } from "./actions/languageActions";
import { saveToLocalStorage, setCurrentLanguage as setStoredLanguage, getLegacyLanguage, removeLegacyLanguage, removeCurrentLanguage } from "../services/localStorage";
import { trackLanguageChanged } from "../mixpanel";
import i18n from "../internationalization/internationalization";
import { STORAGE_KEYS } from "../constants/storageKeys";
import type { Middleware } from "redux";
import type { RootState } from "../types/redux";
import type { LanguageInfo } from "../types/language";

interface LanguagePayloadItem {
    languages_code: LanguageInfo;
}

const getBrowserLanguage = (): string | null => {
  if (navigator && navigator.language) {
    return navigator.language.split('-')[0];
  }
  return null;
};

const findMatchingLanguageFromCMS = (browserLanguage: string, supportedLanguages: LanguagePayloadItem[]): LanguagePayloadItem | undefined => {
  return supportedLanguages.find(lang => 
    lang.languages_code.code === browserLanguage || 
    lang.languages_code.code.startsWith(browserLanguage)
  );
};

const getDefaultLanguageFromCMS = (supportedLanguages: LanguagePayloadItem[]): string | null => {
  return supportedLanguages[0]?.languages_code.code || null;
};

export const languageMiddleware: Middleware<{}, RootState> = store => next => (action: unknown) => {
    const { type, payload } = action as { type: string; payload: string };
    if (type === SET_CURRENT_LANGUAGE) {
      const state = store.getState();

      const { language: { currentLanguage, appLanguages } } = state;

      const result = next(action);

      // Only track if this is a real user-initiated language change, not initial setup
      if (currentLanguage && currentLanguage !== payload) {
        trackLanguageChanged(currentLanguage, payload);
      }
      saveToLocalStorage(STORAGE_KEYS.LEGACY_LANGUAGE, payload);
      saveToLocalStorage(STORAGE_KEYS.LEGACY_DIRECTION, appLanguages![payload].direction);
      
      i18n.changeLanguage(payload);
      setStoredLanguage(payload);

      return result;
    }
  
    return next(action);
};


export const setInitialLanguageMiddleware: Middleware<{}, RootState> = store => next => (action: unknown) => {
  const { type, payload } = action as { type: string; payload: LanguagePayloadItem[] };
  if (type === SET_LANGUAGES) {
    const state = store.getState();
    
    if (!state.language.appLanguages || Object.keys(state.language.appLanguages).length === 0) {
      const result = next(action);
      
      const storedLanguage = getLegacyLanguage();
      
      if (storedLanguage) {
        const availableLanguages = payload.map((lang: LanguagePayloadItem) => lang.languages_code.code);
        const isLanguageAvailable = availableLanguages.includes(storedLanguage);
        
        if (isLanguageAvailable) {
          i18n.changeLanguage(storedLanguage);
          setStoredLanguage(storedLanguage);
          store.dispatch(setCurrentLanguage(storedLanguage));
          return result;
        } else {
          removeLegacyLanguage();
          removeCurrentLanguage();
        }
      }
      
      const browserLanguage = getBrowserLanguage();

      const availableLanguages = payload.map((lang: LanguagePayloadItem) => lang.languages_code.code);

      let selectedLanguageFromCMS = getDefaultLanguageFromCMS(payload);
      
      if (browserLanguage) {
        const matchingLanguageFromCMS = findMatchingLanguageFromCMS(browserLanguage, payload);
        
        if (matchingLanguageFromCMS) {
          selectedLanguageFromCMS = matchingLanguageFromCMS.languages_code.code;
        }
      }

      i18n.changeLanguage(selectedLanguageFromCMS!);
      setStoredLanguage(selectedLanguageFromCMS!);
      
      store.dispatch(setCurrentLanguage(selectedLanguageFromCMS!));
      return result;
    }
  }

  return next(action);
}
